/**
 * TitleScene — first scene at boot. Future Cartridge revamp (PR 5 of
 * TODOs #24).
 *
 * Wireframe:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │       ┌──────────────────────────┐           │  ← ambient type-color
 *   │       │       poke               │           │    cycle (radial,
 *   │       │  ↑ DM Sans 800 / 56px    │           │    30s loop through
 *   │       └──────────────────────────┘           │    6 type colors)
 *   │                                              │
 *   │       ┌──────────────────────────┐           │
 *   │       │ CONTINUE RUN             │           │
 *   │       │ ⬤  Bulbasaur             │           │  ← save-slot card
 *   │       │    Lv 12 · 23/25 HP      │           │    (glass material)
 *   │       │ ⬤ ⬤ ⬤ · · ·               │           │
 *   │       │ ────────────────────     │           │
 *   │       │ REGION  THIS RUN  LIFETIME           │
 *   │       │ Pewter  3         17 / 12            │
 *   │       └──────────────────────────┘           │
 *   │                                              │
 *   │              ┌──────────────┐                │
 *   │              │   New run    │                │  ← secondary
 *   │              └──────────────┘                │    button
 *   │                                              │
 *   │              build 00faddd                   │  ← DM Mono 9px
 *   └──────────────────────────────────────────────┘
 *
 * Architecture: thin Phaser shell that owns camera bg + input gestures
 * + scene transitions. Title content lives entirely in DOM overlays
 * parented to #game. Matches the orb-chrome + battle-overlay pattern —
 * canvas backdrop is paint, DOM is interactive chrome.
 *
 * Async type-tint: the lead orb + each mini orb's type-tint background
 * uses --lead-type-tint / --mini-type-tint custom properties. At mount
 * they fall through to --type-normal (the CSS default). When @pkmn/dex
 * resolves (already pre-warmed via main.ts's void import) we patch in
 * the correct type tokens — single property write per orb, visually
 * negligible flash (~100-300ms post-mount, same tick as fonts settling).
 *
 * Audio + iOS lifecycle: first user gesture in this scene fires
 * audio.unlock() + starts title music, then plays a select SFX on the
 * tapped CTA. Cold-open commitment preserved — once a CTA is tapped,
 * WorldScene's walkout from the bedroom is silent until the player
 * actually moves (no music auto-resumes).
 */
import Phaser from 'phaser';
import { SPECIES_TO_DEX } from '../battle/teamFromParty';
import { audio } from '../systems/audio';
import { careerStore } from '../state/careerStore';
import { runState } from '../state/runState';
import { saveManager } from '../state/saveManager';
import { seedStore } from '../state/seed';
import { getSlotSummary, type SlotSummary } from './titleSlotData';

declare const __BUILD_SHA__: string;

const DARK_BG = '#0E1014';
// Fade-out target matches DARK_BG / CANVAS_BG_HEX (`#0E1014` = rgb(14, 16, 20))
// so the transition into WorldScene is a clean dark-to-dark fade. Was
// previously rgb(253, 244, 220) (cream PAPER) — a holdover from the Kanto-at-
// Golden-Hour aesthetic before WorldScene's canvas bg migrated to dark.
// Visible regression if these drift apart: fade-to-cream-then-snap-to-dark
// flash on every "New Run" / "Continue."
const FADE_RGB = { r: 14, g: 16, b: 20 };
const FADE_MS = 300;

/** Phase-A taste gate for the voxel diorama (parallel session,
 *  2026-08-02). Not a way to play: it starts no run and writes no save.
 *  Flip to false to re-gate the entry. Owner ruling 2026-08-04: hidden
 *  from the title — the scene + its ?voxel=1 URL entry are untouched. */
const VOXEL_PREVIEW_ENABLED = false;

const NEW_RUN_BOTTOM = 100;
/** Approximate rendered height of the new-run button (DM Sans 14/600 +
 *  11px vertical padding). Used by repositionNewRunButton to avoid
 *  overlap with the slot card on short viewports. */
const NEW_RUN_BTN_HEIGHT = 44;
/** Minimum vertical gap between the slot-card bottom and the new-run
 *  button top when the natural bottom-anchored position would overlap. */
const CARD_BUTTON_GAP = 24;

/** Title-case a lowercase species id for display. 'bulbasaur' → 'Bulbasaur'. */
function titleCase(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

/** Build a BW animated GIF <img> for an orb. Returns null when the
 *  species has no dex mapping (defensive — every v0.1 species should be
 *  in SPECIES_TO_DEX, but skipped offers / restored saves with unknown
 *  species fall through to the type-tint-only orb). 404s fail silently
 *  (the broken-image icon is suppressed by the parent's overflow: hidden
 *  plus the type-tint already provides the species color). */
function makeOrbSprite(species: string): HTMLImageElement | null {
  const dexId = SPECIES_TO_DEX[species];
  if (dexId === undefined) return null;
  const img = document.createElement('img');
  img.src = `/assets/pokemon/orb/${dexId}.gif`;
  img.alt = '';
  img.draggable = false;
  return img;
}

/** Pre-fetch lead + party orb sprites via plain Image() so the browser
 *  HTTP cache is warm by the time the <img> elements in the slot card
 *  request the same URLs. Fire-and-forget; mirrors the PartyOrbs pattern. */
function prewarmSlotSprites(summary: SlotSummary): void {
  const dexIds = new Set<number>();
  if (summary.lead) {
    const id = SPECIES_TO_DEX[summary.lead.species];
    if (id !== undefined) dexIds.add(id);
  }
  for (const m of summary.partyMembers) {
    if (!m) continue;
    const id = SPECIES_TO_DEX[m.species];
    if (id !== undefined) dexIds.add(id);
  }
  for (const id of dexIds) {
    const img = new Image();
    img.src = `/assets/pokemon/orb/${id}.gif`;
  }
}

export class TitleScene extends Phaser.Scene {
  private root!: HTMLElement;
  private ambientEl!: HTMLDivElement;
  private wordmarkEl!: HTMLDivElement;
  private slotCardEl: HTMLButtonElement | null = null;
  private slotMiniOrbs: HTMLDivElement[] = [];
  private newRunBtnEl!: HTMLButtonElement;
  private buildLineEl!: HTMLDivElement;
  private voxelLinkEl: HTMLButtonElement | null = null;
  private mounted = false;
  /** True after the first user interaction. iOS Safari needs this gate
   *  before AudioContext.resume() will work. Idempotent. */
  private gestureSeen = false;
  /** Idempotency latch for fadeOutAndStart — prevents double-fire when
   *  pointer click and keyboard shortcut both trigger in quick succession. */
  private starting = false;
  /** Cached summary at mount — drives layout + aria-label. Re-reading at
   *  click time isn't needed; this scene never observes party mutations. */
  private summary!: SlotSummary;
  /** Bound for add/remove symmetry on the resize listener. */
  private layoutHandler!: () => void;

  constructor() {
    super('TitleScene');
  }

  create(): void {
    this.cameras.main.setBackgroundColor(DARK_BG);

    this.summary = getSlotSummary({
      getParty: () => runState.getParty(),
      getPosition: () => runState.getPosition(),
      getDraftedSpecies: () => runState.getDraftedSpecies(),
      getTotals: () => careerStore.getTotals(),
    });

    this.mountDOM();
    this.layoutHandler = () => this.layout();
    this.scale.on('resize', this.layoutHandler);

    // Async type-tint patch — fires-and-forgets. The dex chunk is already
    // pre-warmed in main.ts so this usually resolves before first paint.
    void this.patchTypeTints();

    // First-gesture handler — any pointer/key in this scene unlocks audio
    // + starts title music.
    this.input.on('pointerdown', () => this.handleFirstGesture());
    this.input.keyboard?.on('keydown', () => this.handleFirstGesture());

    // Keyboard shortcut: Enter / Space → primary action (CONTINUE if
    // save exists, else NEW RUN). Defaults to "the more useful path".
    const triggerPrimary = (): void => {
      if (this.summary.hasSave) this.continueRun();
      else this.startNewRun();
    };
    this.input.keyboard?.on('keydown-ENTER', triggerPrimary);
    this.input.keyboard?.on('keydown-SPACE', triggerPrimary);

    // SHUTDOWN cleanup — Phaser fires this on scene.start(other). The
    // DOM nodes are children of #game (not the scene), so they would
    // leak into the next scene's render without this teardown.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  // ─── DOM mount ───────────────────────────────────────────────────────

  private mountDOM(): void {
    const gameEl = document.getElementById('game');
    if (!gameEl) return;
    this.root = gameEl;
    this.mounted = true;

    // Ambient cycle — full-bleed, behind everything.
    this.ambientEl = document.createElement('div');
    this.ambientEl.className = 'title-ambient-cycle';
    gameEl.appendChild(this.ambientEl);

    // Wordmark — anchored top-center.
    this.wordmarkEl = document.createElement('div');
    this.wordmarkEl.className = 'title-wordmark';
    this.wordmarkEl.textContent = 'poke';
    gameEl.appendChild(this.wordmarkEl);

    // Slot card — only when a party exists.
    if (this.summary.hasSave) {
      // Pre-warm the BW orb sprites in the browser cache BEFORE the
      // slot card's <img> elements ask for them. Otherwise the first
      // paint may show empty type-tinted bubbles while the network
      // catches up.
      prewarmSlotSprites(this.summary);
      this.slotCardEl = this.makeSlotCard(this.summary);
      gameEl.appendChild(this.slotCardEl);
    }

    // New run button — always present.
    this.newRunBtnEl = this.makeNewRunButton();
    gameEl.appendChild(this.newRunBtnEl);

    // Build SHA footer.
    this.buildLineEl = document.createElement('div');
    this.buildLineEl.className = 'title-build-line';
    this.buildLineEl.textContent = `build ${__BUILD_SHA__}`;
    gameEl.appendChild(this.buildLineEl);

    // Voxel diorama preview (parallel session's Phase-A taste gate).
    // It used to ride in the New Run mode picker; that picker is gone
    // (there is only one way to play now), so it lives here instead —
    // dev chrome next to the build stamp, which is what it is. Flip
    // VOXEL_PREVIEW_ENABLED to re-gate; delete this block plus
    // src/voxel/ to revert the experiment whole.
    if (VOXEL_PREVIEW_ENABLED) {
      const voxel = document.createElement('button');
      voxel.type = 'button';
      voxel.className = 'title-dev-link';
      voxel.textContent = 'voxel preview';
      voxel.addEventListener('click', () => {
        this.handleFirstGesture();
        this.startVoxelPreview();
      });
      gameEl.appendChild(voxel);
      this.voxelLinkEl = voxel;
    }

    this.layout();
  }

  private makeSlotCard(summary: SlotSummary): HTMLButtonElement {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'title-slot-card';
    if (summary.criticalAny) card.setAttribute('data-critical-any', 'true');
    if (summary.lead) {
      card.setAttribute(
        'aria-label',
        `Continue run with ${titleCase(summary.lead.species)} at level ${summary.lead.level}`,
      );
    } else {
      card.setAttribute('aria-label', 'Continue run');
    }

    // CONTINUE RUN small-caps label.
    const label = document.createElement('div');
    label.className = 'title-slot-card__label';
    label.textContent = 'CONTINUE RUN';
    card.appendChild(label);

    // Lead row: orb + name + HP line.
    const leadRow = document.createElement('div');
    leadRow.className = 'title-slot-card__lead-row';

    const leadOrb = document.createElement('div');
    leadOrb.className = 'title-slot-card__lead-orb';
    if (summary.lead) {
      const sprite = makeOrbSprite(summary.lead.species);
      if (sprite) leadOrb.appendChild(sprite);
    }
    leadRow.appendChild(leadOrb);

    const leadText = document.createElement('div');
    leadText.className = 'title-slot-card__lead-text';

    const name = document.createElement('div');
    name.className = 'title-slot-card__name';
    name.textContent = summary.lead ? titleCase(summary.lead.species) : '—';
    leadText.appendChild(name);

    const hp = document.createElement('div');
    hp.className = 'title-slot-card__hp';
    if (summary.lead) {
      hp.textContent = `Lv ${summary.lead.level} · ${summary.lead.hpCurrent}/${summary.lead.hpMax} HP`;
      if (summary.criticalLead) hp.setAttribute('data-critical-lead', 'true');
    }
    leadText.appendChild(hp);

    leadRow.appendChild(leadText);
    card.appendChild(leadRow);

    // Mini party strip — 6 orbs, null members render as empty placeholders.
    const miniRow = document.createElement('div');
    miniRow.className = 'title-slot-card__mini-row';
    this.slotMiniOrbs = [];
    for (let i = 0; i < 6; i++) {
      const orb = document.createElement('div');
      orb.className = 'title-mini-orb';
      const m = summary.partyMembers[i];
      if (m) {
        const sprite = makeOrbSprite(m.species);
        if (sprite) orb.appendChild(sprite);
      } else {
        orb.setAttribute('data-empty', 'true');
      }
      this.slotMiniOrbs.push(orb);
      miniRow.appendChild(orb);
    }
    card.appendChild(miniRow);

    // Divider.
    const divider = document.createElement('div');
    divider.className = 'title-slot-card__divider';
    card.appendChild(divider);

    // Stats row.
    const stats = document.createElement('div');
    stats.className = 'title-slot-card__stats';
    stats.appendChild(this.makeStatCell('REGION', summary.region));
    stats.appendChild(this.makeStatCell('THIS RUN', String(summary.drafted), true));
    stats.appendChild(this.makeLifetimeCell(summary.total.drafts, summary.total.deaths));
    card.appendChild(stats);

    // Click handler — using `click` (not `pointerdown`) because:
    //   (a) Native button activation via ENTER/SPACE fires `click` only.
    //   (b) TitleScene is the boot scene, so the iOS synthetic-click-
    //       from-prior-touchend window doesn't apply (no prior scene).
    //   (c) Single handler covers touch + mouse + keyboard.
    // handleFirstGesture fires BEFORE playSfx so iOS AudioContext is
    // unlocked in time; otherwise the very first SFX is silently swallowed.
    card.addEventListener('click', () => {
      this.handleFirstGesture();
      audio.playSfx('select');
      this.continueRun();
    });

    return card;
  }

  private makeStatCell(label: string, value: string, numeric = false): HTMLDivElement {
    const cell = document.createElement('div');
    cell.className = 'title-stat-cell';
    const lbl = document.createElement('div');
    lbl.className = 'title-stat-cell__label';
    lbl.textContent = label;
    cell.appendChild(lbl);
    const val = document.createElement('div');
    val.className = 'title-stat-cell__value';
    if (numeric) val.classList.add('title-stat-cell__value--numeric');
    val.textContent = value;
    cell.appendChild(val);
    return cell;
  }

  private makeLifetimeCell(drafts: number, deaths: number): HTMLDivElement {
    const cell = document.createElement('div');
    cell.className = 'title-stat-cell';
    const lbl = document.createElement('div');
    lbl.className = 'title-stat-cell__label';
    lbl.textContent = 'LIFETIME';
    cell.appendChild(lbl);
    const val = document.createElement('div');
    val.className = 'title-stat-cell__value title-stat-cell__value--numeric';
    // Compact "drafts / deaths" with the slash at --text-tertiary.
    val.appendChild(document.createTextNode(String(drafts)));
    const sep = document.createElement('span');
    sep.className = 'title-stat-cell__separator';
    sep.textContent = '/';
    val.appendChild(sep);
    val.appendChild(document.createTextNode(String(deaths)));
    cell.appendChild(val);
    return cell;
  }

  private makeNewRunButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'title-new-run-button';
    btn.textContent = 'New run';
    btn.setAttribute('aria-label', this.summary.hasSave ? 'Start a new run (your current run will be lost)' : 'New run');
    btn.addEventListener('click', () => {
      this.handleFirstGesture();
      audio.playSfx('select');
      this.startNewRun();
    });
    return btn;
  }

  // ─── Layout ──────────────────────────────────────────────────────────

  private layout(): void {
    if (!this.mounted) return;
    const w = this.scale.width;
    const h = this.scale.height;

    // Ambient cycle fills the canvas.
    Object.assign(this.ambientEl.style, {
      left: '0px',
      top: '0px',
      width: `${w}px`,
      height: `${h}px`,
    });

    // Wordmark — top-center. 100px from top on a typical canvas; shrinks
    // to 12% of height on shorter viewports so it never crowds the slot
    // card on iPhone SE-class screens.
    const wordmarkTop = Math.min(100, Math.round(h * 0.12));
    Object.assign(this.wordmarkEl.style, {
      left: '50%',
      top: `${wordmarkTop}px`,
      transform: 'translateX(-50%)',
    });

    // Slot card — centered horizontally, 24px from canvas edges, max
    // 360px wide. Top anchored to the wordmark with breathing room;
    // overlap with the new-run button is prevented in
    // repositionNewRunButton (which runs after the DOM has laid the
    // card out so we know its measured height).
    if (this.slotCardEl) {
      const cardWidth = Math.min(w - 48, 360);
      const cardTopIdeal = Math.min(240, Math.round(h * 0.30));
      const cardTopMin = wordmarkTop + 80;
      const cardTop = Math.max(cardTopIdeal, cardTopMin);
      Object.assign(this.slotCardEl.style, {
        left: '50%',
        top: `${cardTop}px`,
        width: `${cardWidth}px`,
      });
    }

    // Position the new-run button + build line via the post-layout
    // pass so it can read the slot card's measured height. rAF ensures
    // we wait for the layout engine to apply the styles set above
    // before we measure.
    requestAnimationFrame(() => this.repositionNewRunAndFooter());

    // Build SHA footer — interim placement; repositionNewRunAndFooter
    // re-applies authoritative coordinates once the card is measured.
    Object.assign(this.buildLineEl.style, {
      left: '50%',
      bottom: '16px',
      transform: 'translateX(-50%)',
    });
    if (this.voxelLinkEl) {
      Object.assign(this.voxelLinkEl.style, {
        left: '50%',
        bottom: '32px',
        transform: 'translateX(-50%)',
      });
    }
  }

  /** Place the new-run button at its natural bottom-anchored position
   *  unless that would overlap the slot card, in which case push it
   *  down below the card with CARD_BUTTON_GAP padding. Build line
   *  stays bottom-anchored regardless. */
  private repositionNewRunAndFooter(): void {
    if (!this.mounted) return;
    const h = this.scale.height;

    let useTop: number | null = null;
    if (this.slotCardEl) {
      const cardRect = this.slotCardEl.getBoundingClientRect();
      const rootRect = this.root.getBoundingClientRect();
      const cardBottomInCanvas = cardRect.bottom - rootRect.top;
      const desiredButtonTop = cardBottomInCanvas + CARD_BUTTON_GAP;
      const naturalButtonTop = h - NEW_RUN_BOTTOM - NEW_RUN_BTN_HEIGHT;
      if (desiredButtonTop > naturalButtonTop) {
        useTop = desiredButtonTop;
      }
    }

    if (useTop !== null) {
      Object.assign(this.newRunBtnEl.style, {
        left: '50%',
        top: `${useTop}px`,
        bottom: '',
        transform: 'translateX(-50%)',
      });
    } else {
      Object.assign(this.newRunBtnEl.style, {
        left: '50%',
        top: '',
        bottom: `${NEW_RUN_BOTTOM}px`,
        transform: 'translateX(-50%)',
      });
    }
  }

  // ─── Async type-tint patch ───────────────────────────────────────────

  private async patchTypeTints(): Promise<void> {
    let dexMod: typeof import('@pkmn/dex');
    try {
      dexMod = await import('@pkmn/dex');
    } catch {
      // dex chunk failed to load — orbs stay at --type-normal, ambient
      // cycle stays at fire under reduced motion. Gameplay still works.
      return;
    }
    const { Dex } = dexMod;
    // Component may have torn down while the chunk was loading.
    if (!this.mounted) return;

    const typeFor = (species: string): string => {
      const data = Dex.species.get(species);
      const t = (data?.types?.[0] ?? 'Normal').toLowerCase();
      return `var(--type-${t})`;
    };

    if (this.slotCardEl && this.summary.lead) {
      this.slotCardEl.style.setProperty('--lead-type-tint', typeFor(this.summary.lead.species));
    }

    for (let i = 0; i < this.slotMiniOrbs.length; i++) {
      const m = this.summary.partyMembers[i];
      const orb = this.slotMiniOrbs[i];
      if (!orb || !m) continue;
      orb.style.setProperty('--mini-type-tint', typeFor(m.species));
    }

    // Reduced-motion ambient fallback: lock the cycle to the player's
    // most-drafted type. Lifetime data (careerStore) — fresh users with
    // no entries fall through to --type-fire (the CSS default).
    const mostDrafted = this.computeMostDraftedSpecies();
    if (mostDrafted) {
      this.ambientEl.style.setProperty('--title-ambient-static', typeFor(mostDrafted));
    }
  }

  /** Returns the species id with the highest lifetime draft count, or
   *  null if the career graveyard is empty. Ties broken arbitrarily
   *  (Map iteration order = insertion order, which is per-species
   *  first-seen order). */
  private computeMostDraftedSpecies(): string | null {
    const agg = careerStore.aggregateBySpecies();
    let best: { species: string; drafted: number } | null = null;
    for (const [species, { drafted }] of agg) {
      if (best === null || drafted > best.drafted) {
        best = { species, drafted };
      }
    }
    return best?.species ?? null;
  }

  // ─── Audio + navigation ──────────────────────────────────────────────

  private handleFirstGesture(): void {
    if (this.gestureSeen) return;
    this.gestureSeen = true;
    audio.unlock();
    void audio.playMusic(this, 'title', { fadeMs: 1200 });
  }

  private continueRun(): void {
    // handleFirstGesture already fired in the click/keyboard path before
    // we got here — keep it idempotent here for the keyboard-shortcut
    // path (ENTER/SPACE) which bypasses the DOM pointerdown listeners.
    this.handleFirstGesture();
    this.fadeOutAndStart('WorldScene');
  }

  private startNewRun(): void {
    this.handleFirstGesture();
    if (this.summary.hasSave) {
      const ok = window.confirm('Start a new run? Your current run will be lost.');
      if (!ok) return;
    }
    this.beginRun();
  }

  /** Start the run. There is no mode choice: wild Pokémon are visible
   *  in the world and you walk into them. That is simply how the game
   *  works, so it is not a setting, not a picker, and not named
   *  (owner ruling 2026-08-03 — "roam is the only option ... its just
   *  how the game is"). The picker this replaced offered Classic vs
   *  Roam; its history is in git if the alternative ever needs
   *  reviving. */
  private beginRun(): void {
    audio.playSfx('select');
    // Always clear state — defends against orphan saves where party is
    // empty but position is non-null (e.g., a partial wipe). The
    // confirm dialog only fires when hasSave is true (party non-empty);
    // when it's false we skip the prompt but STILL clear so a stale
    // position doesn't resume the player into the previous run's map.
    saveManager.clear();
    runState.reset();
    seedStore.reset();
    seedStore.initFresh();
    this.fadeOutAndStart('WorldScene');
  }

  /** Voxel diorama preview. Deliberately NOT beginRun's path: no
   *  saveManager.clear(), no runState.reset(), no seed roll. Opening
   *  the viewer and exiting it leaves an existing run exactly as it
   *  was. The scene is registered on demand so its code (and the only
   *  WebGL in the project) stays out of the eager entry chunk — the
   *  same dynamic-import posture @pkmn/sim uses. */
  private startVoxelPreview(): void {
    audio.playSfx('select');
    if (this.starting) return;
    this.starting = true;
    void (async () => {
      if (!this.scene.get('VoxelPreviewScene')) {
        const { VoxelPreviewScene } = await import('./VoxelPreviewScene');
        this.scene.add('VoxelPreviewScene', VoxelPreviewScene, false);
      }
      await this.fadeOut();
      this.scene.start('VoxelPreviewScene');
    })();
  }

  private fadeOut(): Promise<void> {
    return new Promise((resolve) => {
      this.cameras.main.fadeOut(FADE_MS, FADE_RGB.r, FADE_RGB.g, FADE_RGB.b);
      this.cameras.main.once('camerafadeoutcomplete', () => resolve());
    });
  }

  private fadeOutAndStart(target: string): void {
    if (this.starting) return;
    this.starting = true;
    void this.fadeOut().then(() => {
      this.scene.start(target);
    });
  }

  // ─── Teardown ────────────────────────────────────────────────────────

  private teardown(): void {
    this.mounted = false;
    this.scale.off('resize', this.layoutHandler);
    this.ambientEl?.remove();
    this.wordmarkEl?.remove();
    this.slotCardEl?.remove();
    this.newRunBtnEl?.remove();
    this.buildLineEl?.remove();
    this.voxelLinkEl?.remove();
    this.voxelLinkEl = null;
    this.slotMiniOrbs = [];
    this.slotCardEl = null;
  }
}
